import { forwardRef } from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/**
 * Avatar — user va tenant logo.
 * Fallback — initials Aurora gradient fonida.
 *
 * Variantlar:
 *   - shape: circle (user) / rounded (tenant logo)
 *   - size: xs/sm/md/lg/xl
 */
const avatarVariants = cva(
  "relative flex shrink-0 overflow-hidden bg-[var(--bg-elevated)] text-[var(--text-primary)]",
  {
    variants: {
      shape: {
        circle: "rounded-full",
        rounded: "rounded-md",
      },
      size: {
        xs: "h-5 w-5 text-[10px]",
        sm: "h-7 w-7 text-xs",
        md: "h-9 w-9 text-sm",
        lg: "h-12 w-12 text-base",
        xl: "h-16 w-16 text-lg",
      },
    },
    defaultVariants: { shape: "circle", size: "md" },
  },
);

export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {}

export const Avatar = forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(({ className, shape, size, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(avatarVariants({ shape, size }), className)}
    {...props}
  />
));
Avatar.displayName = "Avatar";

export const AvatarImage = forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full object-cover", className)}
    {...props}
  />
));
AvatarImage.displayName = "AvatarImage";

export interface AvatarFallbackProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> {
  /** Aurora gradient bg (default true). Off bo'lsa — surface fon */
  gradient?: boolean;
}

export const AvatarFallback = forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(({ className, gradient = true, style, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center font-semibold uppercase tracking-tight",
      gradient ? "text-white" : "bg-[var(--bg-elevated)] text-[var(--text-primary)]",
      className,
    )}
    style={
      gradient
        ? {
            background:
              "linear-gradient(135deg, var(--chart-1) 0%, var(--chart-2) 50%, var(--chart-6) 100%)",
            ...style,
          }
        : style
    }
    {...props}
  />
));
AvatarFallback.displayName = "AvatarFallback";

/**
 * Helper — full name'dan initials chiqarish (max 2 harf).
 * Aliya Karimova → "AK"
 * Decopool → "D"
 */
export function getInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
